import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type { Errors, RegisterFormState, SelectOption } from '@decub8/ui';
import { RegisterFormProps } from '@decub8/ui';
import { useRouter } from 'next/router';

import { api_client } from '@src/bootstrap/index';
import { CONTENT, IS_PROD } from '@src/config';
import { EU_COUNTRIES } from '@src/constants';
import { useGlobalContext } from '@src/hooks/useGlobalContext';
import { USER } from '@src/services/user';
import { getUnsupportedCountrys } from '@src/utils/getters/user';
import {
    newAnalyticsEvent,
    validateEmail,
    validatePassword,
} from '@src/utils/user';

import { useAppSelector } from '../..';

export const useRegister = (): RegisterFormProps => {
    const [state, setState] = useState<RegisterFormState>({
        email: '',
        password: '',
        country: undefined,
        acceptTerms: false,
    });
    const [unsupported_countrys, setUnsupportedCountries] = useState([]);
    const { user } = useAppSelector((state) => state?.auth) || {};
    const [errors, setErrors] = useState<Errors>();
    const [loading, setLoading] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const router = useRouter();
    const { referred_by } = router.query;

    const { has_eu_restriction } = useGlobalContext();

    const is_country_supported = state.country?.label
        ? !unsupported_countrys.includes(state.country?.label)
        : true;

    // If state.country is undefined, we fallback to false
    const is_europe =
        has_eu_restriction && state.country?.id
            ? EU_COUNTRIES.has(state.country.id.toUpperCase())
            : false;

    const disabled =
        state?.email === '' ||
        state?.password === '' ||
        state?.country?.label === undefined ||
        !state?.acceptTerms ||
        (errors?.email || errors?.password ? true : false) ||
        (is_europe ? false : !is_country_supported);

    useEffect(() => {
        const validEmail = validateEmail(state.email);
        if (!validEmail) {
            setErrors({ ...errors, email: 'Please enter valid email' });
        } else {
            setErrors({ ...errors, email: '' });
        }
    }, [state.email]);

    useEffect(() => {
        const validPassword = validatePassword(state.password);

        if (!validPassword) {
            setErrors({ ...errors, password: 'Please enter valid password' });
        } else {
            setErrors({ ...errors, password: '' });
        }
    }, [state.password]);

    useEffect(() => {
        if (user) router.push('/');
    }, [user, router]);

    const handleChange = (
        name: string,
        val: string | boolean | SelectOption,
    ) => {
        setState({ ...state, [name]: val });
    };

    const handleRegister = useCallback(async () => {
        const { email, password } = state;
        const country = state.country.label;
        setLoading(true);

        try {
            let captcha: string;
            if (IS_PROD) {
                captcha = await executeRecaptcha('login');
            }
            const {
                register: { session },
            } = (await api_client.mutate({
                mutation: USER.REGISTER,
                variables: {
                    email: email.toLowerCase(),
                    password,
                    country,
                    referred_by,
                    captcha,
                },
            })) as { register: { session: string } };
            localStorage.setItem('decubate_session', session);
            if (referred_by) {
                router.push(
                    '/verify/email?email=' +
                        encodeURIComponent(email) +
                        '&referred=true',
                );
            } else {
                router.push('/verify/email?email=' + encodeURIComponent(email));
            }

            newAnalyticsEvent('create_account');
        } catch (err) {
            setErrors({ ...errors, form: err.message });
        } finally {
            setLoading(false);
        }
    }, [state, setState, setLoading, setErrors, executeRecaptcha]);

    useEffect(() => {
        const fetchUnsupportedCountries = async () => {
            const data = await getUnsupportedCountrys();
            setUnsupportedCountries(data);
        };
        fetchUnsupportedCountries();
    }, []);

    return {
        disabled,
        errors,
        loading,
        policy: CONTENT.privacyPolicy,
        state,
        terms: CONTENT.termsAndConditions,
        className: 'w-full',
        handleLoginClick: () => router.push('/login'),
        handleChange,
        handleRegister,
        unsupported_country_data:
            !is_europe && !is_country_supported
                ? {
                      company_name: CONTENT.companyName,
                  }
                : undefined,
        title: 'Create account',
        alert: is_europe
            ? {
                  content: (
                      <span>
                          We are temporarily pausing new EU registrations while
                          we finalize our MiCAR license. We appreciate your
                          patience and remain committed to compliance.{' '}
                          <a
                              target="_blank"
                              className="text-warning hover:underline cursor-pointer"
                              href={`https://www.decubate.com/blog/decubate-micar-ensuring-compliance-and-security-for-our-users`}
                          >
                              Learn more here.
                          </a>
                      </span>
                  ),
                  variant: 'warning',
                  title: 'Important information',
              }
            : undefined,
    };
};
